import { useMemo, useState } from 'react';

import {
  Bell,
  CheckCheck,
  ClipboardList,
  FileWarning,
  Inbox,
  Megaphone,
  Receipt,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import type {
  FiltroDaCaixa,
  Notificacao,
  PrioridadeDeNotificacao,
} from '@/modulos/notificacoes/contrato';
import { rotuloDoTipo } from '@/modulos/notificacoes/contrato';
import {
  mensagemDoErro,
  useCaixaDeNotificacoes,
  useMarcarComoLida,
  useMarcarTodasComoLidas,
} from '@/modulos/notificacoes/dados';
import { cn } from '@/nucleo/utilitarios';
import { DataPagination } from '@/ui/DataPagination';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { Button } from '@/ui/base/button';
import { Card } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { Skeleton } from '@/ui/base/skeleton';
import { Switch } from '@/ui/base/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';
import { resolveDateRange, type DateRangeValue } from '@/ui/dateRangePreset';

import {
  PRIORIDADES_DE_NOTIFICACAO,
  TOKEN_DE_PRIORIDADE,
  formatarDataHoraAbsoluta,
  rotuloDaPrioridade,
  rotuloDoGrupoDeData,
  tempoRelativo,
  type CategoriaDeNotificacao,
} from './contrato';

const TAMANHO_DA_PAGINA = 5;

/** Ícone dos tipos que o credenciado mais recebe; o resto fica com o sino. */
const ICONE_DO_TIPO: Readonly<Record<string, LucideIcon>> = {
  PENDENCIA_DOCUMENTO: FileWarning,
  DOCUMENTACAO_PENDENTE: FileWarning,
  COBRANCA_GERADA: Receipt,
  MULTA_APLICADA: Receipt,
  NOVA_SOLICITACAO: ClipboardList,
  SOLICITACAO_EM_ANALISE: ClipboardList,
  PARAMETRO_VIGENCIA: Megaphone,
};

type Aba = 'todas' | CategoriaDeNotificacao;

/**
 * As abas e os seus painéis nascem da mesma lista: cada `TabsTrigger` aponta,
 * por `aria-controls`, para um `TabsContent` que existe (regra de interface 5).
 * Sem o painel, o leitor de tela anuncia uma aba que não controla nada.
 */
const ABAS: { valor: Aba; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'operacional', rotulo: 'Operacionais' },
  { valor: 'comunicacional', rotulo: 'Comunicacionais' },
];

const SEM_PERIODO: DateRangeValue = { preset: 'ALL' };

function agruparPorData(itens: Notificacao[]): { rotulo: string; itens: Notificacao[] }[] {
  const grupos = new Map<string, Notificacao[]>();
  for (const item of itens) {
    const rotulo = rotuloDoGrupoDeData(item.criadaEm);
    const atual = grupos.get(rotulo);
    if (atual) {
      atual.push(item);
    } else {
      grupos.set(rotulo, [item]);
    }
  }
  return Array.from(grupos.entries()).map(([rotulo, grupoItens]) => ({
    rotulo,
    itens: grupoItens,
  }));
}

/**
 * `/credenciado/notificacoes` — central de notificações do credenciado.
 *
 * Dado real, da caixa de quem está logado (`bff/v1/notificacoes`, a mesma do
 * sino e das centrais das áreas internas). Filtro, paginação e contador vêm do
 * servidor; o contador do cabeçalho é o da caixa inteira, como o do sino.
 *
 * MARCAR COMO LIDA GRAVA. Abrir uma notificação não lida a marca como lida, e
 * "Marcar todas como lidas" marca a caixa inteira; a lista, o contador e o sino
 * se atualizam pela resposta da API. Aviso já lido não é botão: não há o que
 * fazer com ele (regra 7 de interface).
 *
 * "IR PARA A TELA RELACIONADA" NÃO EXISTE AINDA: a API não calcula o destino do
 * aviso (`apps/api/src/modulos/notificacoes/README.md`), então nenhum aviso
 * navega.
 */
export function NotificacoesPage() {
  const [aba, setAba] = useState<Aba>('todas');
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const [periodo, setPeriodo] = useState<DateRangeValue>(SEM_PERIODO);
  const intervalo = useMemo(() => resolveDateRange(periodo), [periodo]);
  const [somenteNaoLidas, setSomenteNaoLidas] = useState(false);

  const filtro = useMemo<FiltroDaCaixa>(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      categoria: aba === 'todas' ? undefined : aba,
      busca: filtros.busca || undefined,
      tipo: filtros.tipo || undefined,
      prioridade: (filtros.prioridade || undefined) as PrioridadeDeNotificacao | undefined,
      somenteNaoLidas: somenteNaoLidas || undefined,
      criadaDe: intervalo.from || undefined,
      criadaAte: intervalo.to || undefined,
    }),
    [
      pagina,
      aba,
      filtros.busca,
      filtros.tipo,
      filtros.prioridade,
      somenteNaoLidas,
      intervalo.from,
      intervalo.to,
    ],
  );

  const caixa = useCaixaDeNotificacoes(filtro);
  const marcarComoLida = useMarcarComoLida();
  const marcarTodas = useMarcarTodasComoLidas();

  const carregando = caixa.isLoading;
  const comErro = caixa.isError;
  // Só há contador com o dado carregado. Enquanto carrega, ou se a consulta
  // falhou, ninguém apurou nada: o cabeçalho não diz "Todas as notificações
  // lidas" (seria afirmar o que não foi medido) e o botão de marcar não aparece.
  const contador = comErro ? undefined : caixa.data?.naoLidas;
  const descricaoDoContador =
    contador === undefined
      ? undefined
      : contador > 0
        ? `${contador} não lida${contador > 1 ? 's' : ''}`
        : 'Todas as notificações lidas';

  const temFiltro = Boolean(
    filtros.busca ||
    filtros.tipo ||
    filtros.prioridade ||
    somenteNaoLidas ||
    intervalo.from ||
    intervalo.to,
  );

  function limparFiltros() {
    setPagina(1);
    setFiltros({});
    setPeriodo(SEM_PERIODO);
    setSomenteNaoLidas(false);
  }

  function abrirNotificacao(notificacao: Notificacao) {
    if (notificacao.lida) return;
    marcarComoLida.mutate(notificacao.id, {
      onError: (erro) =>
        toast.error('A notificação não foi marcada como lida', {
          description: mensagemDoErro(erro, 'Tente de novo em instantes.'),
        }),
    });
  }

  function marcarTodasComoLidas() {
    marcarTodas.mutate(undefined, {
      onError: (erro) =>
        toast.error('As notificações não foram marcadas como lidas', {
          description: mensagemDoErro(erro, 'Tente de novo em instantes.'),
        }),
    });
  }

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Buscar por título ou mensagem…',
      ariaLabel: 'Buscar notificações',
      flex: true,
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      options: (caixa.data?.tipos ?? []).map((tipo) => ({
        value: tipo,
        label: rotuloDoTipo(tipo),
      })),
      width: 'w-full sm:w-[220px]',
    },
    {
      key: 'prioridade',
      label: 'Prioridade',
      type: 'select',
      options: PRIORIDADES_DE_NOTIFICACAO.map((opcao) => ({
        value: opcao.value,
        label: opcao.label,
      })),
      width: 'w-full sm:w-[160px]',
    },
  ];

  const grupos = useMemo(() => agruparPorData(caixa.data?.itens ?? []), [caixa.data]);

  // A lista é a mesma nas três abas: o que muda é o filtro de categoria, que
  // já vai na consulta. Ela entra num `TabsContent` por aba só para cada aba
  // ter o painel que anuncia.
  const lista = comErro ? (
    <ErrorState
      variant="load-failed"
      description="Não foi possível carregar as notificações agora. Tente novamente em instantes."
      primaryAction={{
        label: 'Tentar de novo',
        onClick: () => {
          void caixa.refetch();
        },
      }}
    />
  ) : carregando ? (
    <div role="status" aria-live="polite" className="space-y-2">
      {Array.from({ length: 4 }).map((_, indice) => (
        <Card key={indice} className="flex items-start gap-4 p-4">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </Card>
      ))}
      <p className="sr-only">Carregando notificações.</p>
    </div>
  ) : !caixa.data || caixa.data.itens.length === 0 ? (
    <EmptyState
      icon={Inbox}
      title={temFiltro ? 'Nenhuma notificação encontrada' : 'Nenhuma notificação'}
      description={
        temFiltro
          ? 'Nenhuma notificação atende a esses filtros. Tente afrouxar a busca.'
          : 'Você será avisado por aqui quando houver alguma novidade.'
      }
    />
  ) : (
    <div className="space-y-6">
      {grupos.map((grupo) => (
        <div key={grupo.rotulo} className="space-y-2">
          <p className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {grupo.rotulo}
          </p>
          <ul className="space-y-2">
            {grupo.itens.map((item) => (
              <li key={item.id}>
                <ItemDeNotificacao notificacao={item} onAbrir={() => abrirNotificacao(item)} />
              </li>
            ))}
          </ul>
        </div>
      ))}

      <DataPagination
        page={pagina}
        perPage={TAMANHO_DA_PAGINA}
        total={caixa.data.total}
        onPageChange={setPagina}
        onPerPageChange={() => {}}
        opcoesPorPagina={[TAMANHO_DA_PAGINA]}
      />
    </div>
  );

  return (
    <div className="mx-auto w-full min-w-0 max-w-3xl space-y-6">
      <PageHeader title="Notificações" description={descricaoDoContador}>
        {contador !== undefined && contador > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={marcarTodasComoLidas}
            disabled={marcarTodas.isPending}
          >
            <CheckCheck className="h-4 w-4 mr-2" aria-hidden="true" />
            Marcar todas como lidas
          </Button>
        )}
      </PageHeader>

      <Tabs
        value={aba}
        onValueChange={(valor) => {
          setAba(valor as Aba);
          setPagina(1);
        }}
        className="space-y-6"
      >
        <TabsList>
          {ABAS.map(({ valor, rotulo }) => (
            <TabsTrigger key={valor} value={valor}>
              {rotulo}
            </TabsTrigger>
          ))}
        </TabsList>

        <FilterBar
          fields={campos}
          values={filtros}
          hasActiveFilters={temFiltro}
          onChange={(chave, valor) => {
            setPagina(1);
            setFiltros((atual) => ({ ...atual, [chave]: valor }));
          }}
          onClear={limparFiltros}
        >
          <DateRangePresetFilter
            label="Recebida em"
            value={periodo}
            onChange={(valor) => {
              setPagina(1);
              setPeriodo(valor);
            }}
          />
          <div className="flex items-center gap-2 pb-2.5">
            <Switch
              id="somente-nao-lidas"
              checked={somenteNaoLidas}
              onCheckedChange={(valor) => {
                setPagina(1);
                setSomenteNaoLidas(valor);
              }}
            />
            <Label
              htmlFor="somente-nao-lidas"
              className="normal-case tracking-normal font-normal text-foreground"
            >
              Somente não lidas
            </Label>
          </div>
        </FilterBar>

        {ABAS.map(({ valor }) => (
          <TabsContent key={valor} value={valor} className="mt-0">
            {lista}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function ItemDeNotificacao({
  notificacao,
  onAbrir,
}: {
  notificacao: Notificacao;
  onAbrir: () => void;
}) {
  const Icone = ICONE_DO_TIPO[notificacao.tipo] ?? Bell;
  const classes = cn(
    'flex w-full items-start gap-4 rounded-xl border border-border bg-card p-4 text-left transition-colors',
    !notificacao.lida && 'border-l-4 border-l-primary bg-primary/5 hover:bg-primary/10',
  );

  const conteudo = (
    <>
      <span className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
        <Icone className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span
          className={cn(
            'absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card',
            TOKEN_DE_PRIORIDADE[notificacao.prioridade ?? 'baixa'],
          )}
          aria-hidden="true"
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-2">
          <span className={cn('text-sm leading-tight', !notificacao.lida && 'font-semibold')}>
            {notificacao.titulo}
          </span>
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
            {tempoRelativo(notificacao.criadaEm)}
          </span>
        </span>
        <span className="mt-1 block text-xs text-muted-foreground">{notificacao.mensagem}</span>
        <span className="mt-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            {rotuloDoTipo(notificacao.tipo)}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatarDataHoraAbsoluta(notificacao.criadaEm)}
          </span>
        </span>
      </span>
      {!notificacao.lida && (
        <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      )}
    </>
  );

  // Já lida: nada a fazer com ela, então não é botão (regra 7 de interface).
  if (notificacao.lida) {
    return <div className={classes}>{conteudo}</div>;
  }

  const prioridade = notificacao.prioridade
    ? `, prioridade ${rotuloDaPrioridade(notificacao.prioridade)}`
    : '';
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={classes}
      aria-label={`${notificacao.titulo}, não lida${prioridade}. Marcar como lida`}
    >
      {conteudo}
    </button>
  );
}

export default NotificacoesPage;
